import { lazyWithRetry } from "@/utils/lazyWithRetry";
import { lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Hero } from "@/components/sections/Hero";
import { ScrollToTop } from "@/components/ui/scroll-to-top";

// Lazy load ALL below-hero sections for better FCP/LCP
const PopularCategories = lazyWithRetry(() => import("@/components/sections/PopularCategories").then(m => ({ default: m.PopularCategories })));
const AppPreviewBanner = lazyWithRetry(() => import("@/components/sections/AppPreviewBanner").then(m => ({ default: m.AppPreviewBanner })));
const HomeLeverantorerSection = lazyWithRetry(() =>
  import("@/components/sections/HomeLeverantorerSection").then(m => ({ default: m.HomeLeverantorerSection }))
);
const Articles = lazyWithRetry(() => import("@/components/sections/Articles").then(m => ({ default: m.Articles })));
const VigselringarSection = lazyWithRetry(() => import("@/components/sections/VigselringarSection").then(m => ({ default: m.VigselringarSection })));
const LocationSection = lazyWithRetry(() => import("@/components/sections/LocationSection").then(m => ({ default: m.LocationSection })));
const InstagramFeedSection = lazyWithRetry(() => import("@/components/sections/InstagramFeedSection").then(m => ({ default: m.InstagramFeedSection })));
const AboutUs = lazyWithRetry(() => import("@/components/sections/AboutUs").then(m => ({ default: m.AboutUs })));
const Features = lazyWithRetry(() => import("@/components/sections/Features").then(m => ({ default: m.Features })));
const WeddingInfoFooter = lazyWithRetry(() => import("@/components/sections/WeddingInfoFooter").then(m => ({ default: m.WeddingInfoFooter })));

// Section-specific fallback heights to prevent CLS when Suspense resolves
const SectionFallback = ({ className = "min-h-[200px]" }: { className?: string }) => <div className={className} />;

export default function Index() {
  return (
    <>
      <Helmet>
        <title>Weddingly – Planera hela ert bröllop enkelt på ett ställe</title>
        <meta name="description" content="Planera ert bröllop smidigt på Weddingly. Hitta leverantörer, erbjudanden, budgetverktyg och checklistor – allt ni behöver samlat på ett ställe." />
        <link rel="canonical" href="https://weddingly.se/" />
        <link rel="alternate" hrefLang="sv" href="https://weddingly.se/" />
        <link rel="alternate" hrefLang="x-default" href="https://weddingly.se/" />
        <meta property="og:title" content="Weddingly – Planera hela ert bröllop enkelt på ett ställe" />
        <meta property="og:description" content="Planera ert bröllop smidigt på Weddingly. Hitta leverantörer, erbjudanden, budgetverktyg och checklistor." />
        <meta property="og:url" content="https://weddingly.se/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://weddingly.se/images/og-default.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Weddingly – Planera hela ert bröllop enkelt på ett ställe" />
        <meta name="twitter:description" content="Planera ert bröllop smidigt på Weddingly. Hitta leverantörer, erbjudanden, budgetverktyg och checklistor." />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://weddingly.se/#organization",
              "name": "Weddingly",
              "url": "https://weddingly.se",
              "logo": "https://weddingly.se/images/og-default.png",
              "description": "Sveriges bröllopsmarknadsplats – hitta leverantörer, verktyg och inspiration för ert bröllop."
            },
            {
              "@type": "WebSite",
              "@id": "https://weddingly.se/#website",
              "name": "Weddingly",
              "url": "https://weddingly.se",
              "publisher": { "@id": "https://weddingly.se/#organization" },
              "inLanguage": "sv-SE",
              "potentialAction": {
                "@type": "SearchAction",
                "target": "https://weddingly.se/brollopslokaler?q={search_term_string}",
                "query-input": "required name=search_term_string"
              }
            }
          ]
        })}</script>
      </Helmet>
      <Header />
      <Hero />
      <Suspense fallback={<SectionFallback className="min-h-[400px] lg:min-h-[500px]" />}>
        <PopularCategories />
      </Suspense>
      <Suspense fallback={<SectionFallback className="min-h-[500px] lg:min-h-[700px]" />}>
        <AppPreviewBanner />
      </Suspense>
      <Suspense fallback={<SectionFallback className="min-h-[600px] md:min-h-[400px]" />}>
        <HomeLeverantorerSection />
      </Suspense>
      <Suspense fallback={<SectionFallback className="min-h-[400px] lg:min-h-[500px]" />}>
        <Articles />
      </Suspense>
      <Suspense fallback={<SectionFallback className="min-h-[300px] lg:min-h-[400px]" />}>
        <VigselringarSection />
      </Suspense>
      <Suspense fallback={<SectionFallback className="min-h-[300px] lg:min-h-[400px]" />}>
        <InstagramFeedSection />
      </Suspense>
      <Suspense fallback={<SectionFallback className="min-h-[300px] lg:min-h-[400px]" />}>
        <LocationSection />
      </Suspense>
      <main>
        <Suspense fallback={<SectionFallback />}>
          <AboutUs />
        </Suspense>
        <Suspense fallback={<SectionFallback />}>
          <Features />
        </Suspense>
      </main>
      <Suspense fallback={<SectionFallback />}>
        <WeddingInfoFooter />
      </Suspense>
      <Footer />
      <ScrollToTop />
    </>
  );
}
